import { buildAgentFileURL, normalizeAgentPath } from '@/utils/agent-file-url';
import { createRandomID } from '@/utils/random-id';

export interface WebAgentConfig {
  agent_group_id: string;
  agent_id: string;
  avatar_url?: string;
  system_reminder?: string;
}

export interface WebAgentTemplateCatalog {
  templates: Array<{
    template_id: string;
    name: string;
    model: string;
    provider_type?: string;
  }>;
  stale: boolean;
}

export interface WebChatBootstrap {
  user: {
    id: string;
    email: string;
  };
  agent: WebAgentConfig & {
    maintenance_status?: string;
  };
  fixedConversations?: {
    router?: WebAgentConversationInfo | null;
  } | null;
  display?: {
    agentAvatarURLs: Record<string, string>;
  } | null;
  templateCatalog?: WebAgentTemplateCatalog | null;
  credits?: {
    available_credits: number;
    available_display_credits: number;
    credit_display_divisor: number;
    next_reset_at?: string | null;
    snapshot_at: string;
    maintenance_status: string;
  } | null;
}

export interface WebConversationListItem {
  conversationId: string;
  agentGroupId?: string | null;
  kind?: string | null;
  title: string;
  createdByAgentId?: string | null;
  createdBySessionId?: string | null;
  createdAt: number;
  updatedAt: number;
  messageCount: number;
  targetMessageCount: number;
  lastMessagePreview?: string | null;
  participants: WebAgentConversationParticipant[];
}

export interface WebAgentAssistantActivity {
  agentId?: string | null;
  sessionId?: string | null;
  conversationId?: string | null;
  goal?: string | null;
  action?: string | null;
  displayStrength?: string | null;
  displayPriority?: string | null;
  displayHoldMs?: number | null;
  phase: string;
  summary?: string | null;
  status?: string | null;
  toolCallId?: string | null;
  toolName?: string | null;
  sequence: number;
  updatedAt: number;
}

export type WebAgentConversationEvent =
  | {
      conversationId: string;
      type: 'conversation_upsert';
      conversation: WebAgentConversationInfo;
    }
  | {
      conversationId: string;
      type: 'conversation_delete';
    }
  | {
      conversationId: string;
      messageId?: string | null;
      type: 'message_created' | 'message_updated';
      conversation?: WebAgentConversationInfo | null;
      message?: WebAgentConversationMessage | null;
    }
  | {
      conversationId: string;
      type:
        | 'message_draft_started'
        | 'message_draft_delta'
        | 'message_draft_completed'
        | 'message_draft_cancelled';
      draft: WebAgentConversationMessageDraft;
    };

export interface WebAgentConversationMessageDraft {
  conversationId: string;
  draftId: string;
  toolCallId?: string | null;
  kind: string;
  participantId?: string | null;
  actorType?: string | null;
  userId?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  agentAvatarURL?: string | null;
  sessionId?: string | null;
  roleLabel?: string | null;
  content?: unknown;
  delta?: string | null;
  status: string;
  createdAt?: number | null;
  updatedAt?: number | null;
}

export interface WebAgentConversationInfo {
  conversationId: string;
  agentGroupId?: string | null;
  kind: string;
  title: string;
  status: string;
  activityStatus?: string | null;
  lastRoundError?: string | null;
  createdByAgentId: string;
  createdBySessionId: string;
  participants?: WebAgentConversationParticipant[];
  messageCount: number;
  targetMessageCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface WebAgentConversationParticipant {
  participantId: string;
  conversationId?: string | null;
  actorType: string;
  userId?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  agentAvatarURL?: string | null;
  sessionId?: string | null;
  roleLabel?: string | null;
  state: string;
  wakeOnMessage: boolean;
  notificationMode?: string | null;
  createdAt: number;
  updatedAt: number;
}

function agentConversationBasePath(agentGroupId: string): string {
  const normalized = agentGroupId.trim();
  if (!normalized) {
    throw new Error('agent_group_id is required for agent conversation APIs');
  }
  return `/v1/user/agent-groups/${encodeURIComponent(normalized)}/conversations`;
}

function agentGroupCapabilityRequestsBasePath(agentGroupId: string): string {
  const normalized = agentGroupId.trim();
  if (!normalized) {
    throw new Error('agent_group_id is required for capability request APIs');
  }
  return `/v1/user/agent-groups/${encodeURIComponent(normalized)}/capability-requests`;
}

export interface WebAgentConversationPage {
  data: WebAgentConversationInfo[];
  nextCursor?: string | null;
  hasMore: boolean;
}

export interface WebAgentSendMessageResponse {
  messageId: string;
}

export interface WebAgentStoredMessage {
  messageId: number;
  kind?: string;
  role: string;
  content: string;
  metadata?: unknown;
  toolUseId?: string;
  sessionId?: string;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  createdAt: number;
}

export interface WebAgentMessageSearchResult {
  messageId: number;
  role: string;
  sessionId?: string | null;
  conversationId?: string | null;
  createdAt: number;
  snippet: string;
  score: number;
}

export interface WebAgentConversationSearchResult {
  conversationId: string;
  conversationKind?: string | null;
  title: string;
  messageId?: string | null;
  role: string;
  createdAt: number;
  updatedAt?: number | null;
  snippet: string;
  score: number;
}

export interface WebAgentMessageFileRef {
  refId?: string;
  kind?: string;
  source?: string;
  environmentId?: string;
  path?: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number;
  contentHash?: string;
  displayURL?: string;
  downloadURL?: string;
  thumbnailURL?: string;
  publicShareURL?: string;
  authRequired?: boolean;
  authScope?: string;
  sourceSessionId?: string;
  agentId?: string;
  signature?: string;
  expiresAt?: number;
}

export interface WebAgentMessageContent {
  type: string;
  text?: string;
  url?: string;
  fileRef?: WebAgentMessageFileRef;
  fileRefs?: WebAgentMessageFileRef[];
  fileName?: string;
  mimeType?: string;
  generationPrompt?: string;
  sizeBytes?: number;
  entryKind?: 'file' | 'dir';
}

export interface WebAgentConversationMessage {
  messageId: string;
  kind: string;
  participantId: string;
  actorType: string;
  userId?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  agentAvatarURL?: string | null;
  sessionId?: string | null;
  roleLabel?: string | null;
  content: unknown;
  metadata?: unknown;
  createdAt: number;
}

export interface WebAgentCapabilityRequest {
  requestId: string;
  sourceAgentId: string;
  sourceSessionId: string;
  toolCallId: string;
  operationId: string;
  requestType: string;
  status: string;
  requestPayload: Record<string, unknown>;
  responsePayload: Record<string, unknown>;
  surfaceKind?: string | null;
  surfaceRef: Record<string, unknown>;
  expiresAt?: number | null;
  createdAt: number;
  updatedAt: number;
  completedAt?: number | null;
}

export interface WebAgentSessionTraceToolCall {
  callId?: string;
  name: string;
  status?: string;
  timestamp?: string;
  durationMs?: number;
  input?: string;
  inputTruncated: boolean;
  output?: string;
  outputTruncated: boolean;
}

export interface WebAgentSessionTraceSkillRead {
  path: string;
  timestamp?: string;
}

export interface WebAgentSessionTrace {
  toolCalls: WebAgentSessionTraceToolCall[];
  skillReads: WebAgentSessionTraceSkillRead[];
  usage: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  hasMore: boolean;
}

export interface WebAgentFileEntry {
  path: string;
  kind: 'file' | 'dir';
  size: number;
  modifiedAt: number;
}

export interface WebAgentFileWriteResult {
  path: string;
  size: number;
}

export interface WebAudioTranscriptionResponse {
  text: string;
  languageCode?: string | null;
  durationSeconds?: number | null;
}

type RequestInitWithBody = RequestInit & {
  body?: BodyInit | null;
};

const WILLOW_CONVERSATION_CORE_CLIENT_VERSION = '1.2.25';
const WILLOW_CLIENT_VERSION_HEADER = 'X-Client-Version';
const WILLOW_CLIENT_SOURCE_HEADER = 'X-Client-Source';
const WILLOW_CLIENT_PLATFORM_HEADER = 'X-Client-Platform';
const WILLOW_WEB_CLIENT_SOURCE = 'web';
const WILLOW_WEB_CLIENT_PLATFORM = 'web';
const WILLOW_DEFAULT_CLIENT_CONTEXT_HEADERS = {
  [WILLOW_CLIENT_VERSION_HEADER]: WILLOW_CONVERSATION_CORE_CLIENT_VERSION,
  [WILLOW_CLIENT_SOURCE_HEADER]: WILLOW_WEB_CLIENT_SOURCE,
  [WILLOW_CLIENT_PLATFORM_HEADER]: WILLOW_WEB_CLIENT_PLATFORM,
};

const jsonHeaders = {
  accept: 'application/json',
  'content-type': 'application/json',
};

export const WEB_SSE_IDLE_TIMEOUT_MS = 45_000;

let bootstrapAgentAvatarURLs: Record<string, string> = {};

function updateBootstrapAgentAvatarURLs(
  raw: Record<string, string> | null | undefined
) {
  const next: Record<string, string> = {};
  for (const [agentId, avatarURL] of Object.entries(raw ?? {})) {
    const normalizedAgentId = agentId.trim();
    const normalizedAvatarURL = avatarURL.trim();
    if (normalizedAgentId && normalizedAvatarURL) {
      next[normalizedAgentId] = normalizedAvatarURL;
    }
  }
  bootstrapAgentAvatarURLs = next;
}

function avatarURLForAgentID(agentId: string | null | undefined) {
  const normalizedAgentId = agentId?.trim() || '';
  return normalizedAgentId ? bootstrapAgentAvatarURLs[normalizedAgentId] : '';
}

function normalizeAgentAvatarURL(
  rawAvatarURL: string | null | undefined,
  agentId: string | null | undefined
) {
  return (
    normalizeString(rawAvatarURL) ??
    normalizeString(avatarURLForAgentID(agentId))
  );
}

export class HTTPStatusError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly retryAfterSeconds?: number | null
  ) {
    super(message);
    this.name = 'HTTPStatusError';
  }
}

export function isAgentDBMigratingError(error: unknown): boolean {
  return (
    error instanceof HTTPStatusError &&
    error.status === 503 &&
    (error.code === 'agent_db_migrating' ||
      error.code === 'agent_db_migration_failed')
  );
}

export function isAgentDBMigrationTransientError(error: unknown): boolean {
  return (
    error instanceof HTTPStatusError &&
    error.status === 503 &&
    error.code === 'agent_db_migrating'
  );
}

function errorCode(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null || !('error' in body)) {
    return undefined;
  }
  const value = body.error;
  if (typeof value === 'string') {
    return value;
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof value.code === 'string'
  ) {
    return value.code;
  }
  return undefined;
}

function buildErrorMessage(status: number, body: unknown): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'message' in body.error &&
    typeof body.error.message === 'string'
  ) {
    return body.error.message;
  }

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  return `Request failed (${status})`;
}

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }

  return response.text().catch(() => null);
}

function hasHeader(headers: HeadersInit | undefined, name: string): boolean {
  return headers ? new Headers(headers).has(name) : false;
}

function withWillowClientContextHeaders(headers?: HeadersInit): HeadersInit {
  if (headers instanceof Headers) {
    const next = new Headers(headers);
    for (const [name, value] of Object.entries(
      WILLOW_DEFAULT_CLIENT_CONTEXT_HEADERS
    )) {
      if (!next.has(name)) {
        next.set(name, value);
      }
    }
    return next;
  }
  if (Array.isArray(headers)) {
    const next: [string, string][] = [...headers];
    for (const [name, value] of Object.entries(
      WILLOW_DEFAULT_CLIENT_CONTEXT_HEADERS
    )) {
      if (!hasHeader(headers, name)) {
        next.push([name, value]);
      }
    }
    return next;
  }
  const next: Record<string, string> = { ...headers };
  for (const [name, value] of Object.entries(
    WILLOW_DEFAULT_CLIENT_CONTEXT_HEADERS
  )) {
    if (!hasHeader(headers, name)) {
      next[name] = value;
    }
  }
  return next;
}

async function fetchJSON<T>(
  path: string,
  init?: RequestInitWithBody
): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: withWillowClientContextHeaders(init?.headers),
  });

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body),
      errorCode(body),
      Number(response.headers.get('retry-after')) || null
    );
  }

  return (await response.json()) as T;
}

async function fetchVoid(
  path: string,
  init?: RequestInitWithBody
): Promise<void> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: withWillowClientContextHeaders(init?.headers),
  });

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body),
      errorCode(body),
      Number(response.headers.get('retry-after')) || null
    );
  }
}

function serializeAgentMessageContent(
  content: string | WebAgentMessageContent[]
) {
  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }

  return content.map(block => {
    const payload: {
      type: string;
      text?: string;
      url?: string;
      file_ref?: {
        environment_id?: string;
        path: string;
        source_session_id?: string;
        agent_id?: string;
        signature?: string;
        expires_at?: number;
      };
      file_refs?: Array<{
        environment_id?: string;
        path: string;
        source_session_id?: string;
        agent_id?: string;
        signature?: string;
        expires_at?: number;
      }>;
      file_name?: string;
      mime_type?: string;
      generation_prompt?: string;
      size_bytes?: number;
      entry_kind?: 'file' | 'dir';
    } = {
      type: block.type,
    };

    if (block.text !== undefined) {
      payload.text = block.text;
    }
    if (block.url) {
      payload.url = block.url;
    }
    const fileRefs = [
      ...(block.fileRefs ?? []),
      ...(block.fileRef?.path ? [block.fileRef] : []),
    ].flatMap(ref => {
      const path = ref.path?.trim();
      if (!path) {
        return [];
      }
      return [{ ...ref, path }];
    });
    const uniqueFileRefs = fileRefs.filter((ref, index, refs) => {
      return (
        refs.findIndex(
          candidate =>
            candidate.path === ref.path &&
            (candidate.environmentId?.trim() ?? '') ===
              (ref.environmentId?.trim() ?? '') &&
            (candidate.sourceSessionId?.trim() ?? '') ===
              (ref.sourceSessionId?.trim() ?? '') &&
            (candidate.agentId?.trim() ?? '') === (ref.agentId?.trim() ?? '') &&
            (candidate.signature?.trim() ?? '') ===
              (ref.signature?.trim() ?? '')
        ) === index
      );
    });

    if (uniqueFileRefs.length === 0 && block.fileRef?.path) {
      payload.file_ref = {
        path: block.fileRef.path,
      };
      if (block.fileRef.environmentId) {
        payload.file_ref.environment_id = block.fileRef.environmentId;
      }
      if (block.fileRef.sourceSessionId) {
        payload.file_ref.source_session_id = block.fileRef.sourceSessionId;
      }
      if (block.fileRef.agentId) {
        payload.file_ref.agent_id = block.fileRef.agentId;
      }
      if (block.fileRef.signature) {
        payload.file_ref.signature = block.fileRef.signature;
      }
      if (
        typeof block.fileRef.expiresAt === 'number' &&
        Number.isFinite(block.fileRef.expiresAt)
      ) {
        payload.file_ref.expires_at = block.fileRef.expiresAt;
      }
    }
    if (uniqueFileRefs.length > 0) {
      payload.file_refs = uniqueFileRefs.map(ref => ({
        path: ref.path,
        environment_id: ref.environmentId || undefined,
        source_session_id: ref.sourceSessionId || undefined,
        agent_id: ref.agentId || undefined,
        signature: ref.signature || undefined,
        expires_at:
          typeof ref.expiresAt === 'number' && Number.isFinite(ref.expiresAt)
            ? ref.expiresAt
            : undefined,
      }));
    }
    if (block.fileName) {
      payload.file_name = block.fileName;
    }
    if (block.mimeType) {
      payload.mime_type = block.mimeType;
    }
    if (block.generationPrompt) {
      payload.generation_prompt = block.generationPrompt;
    }
    if (
      typeof block.sizeBytes === 'number' &&
      Number.isFinite(block.sizeBytes)
    ) {
      payload.size_bytes = block.sizeBytes;
    }
    if (block.entryKind) {
      payload.entry_kind = block.entryKind;
    }

    return payload;
  });
}

export { buildAgentFileURL, normalizeAgentPath };

export async function getChatBootstrap(options?: {
  includeTemplateCatalog?: boolean;
}): Promise<WebChatBootstrap> {
  const query = new URLSearchParams();
  if (options?.includeTemplateCatalog) {
    query.set('include', 'template_catalog');
  }
  const path = query.size
    ? `/v1/user/chat-bootstrap?${query.toString()}`
    : '/v1/user/chat-bootstrap';
  const bootstrap = await fetchJSON<
    Omit<WebChatBootstrap, 'fixedConversations'> & {
      fixed_conversations?: {
        router?: AgentConversationResponse | null;
      } | null;
      display?: {
        agent_avatar_urls?: Record<string, string> | null;
      } | null;
      template_catalog?: WebAgentTemplateCatalog | null;
    }
  >(path, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  });
  const agentAvatarURLs = bootstrap.display?.agent_avatar_urls ?? {};
  updateBootstrapAgentAvatarURLs(agentAvatarURLs);

  return {
    user: bootstrap.user,
    agent: bootstrap.agent,
    fixedConversations: {
      router: bootstrap.fixed_conversations?.router
        ? normalizeAgentConversation(bootstrap.fixed_conversations.router)
        : null,
    },
    display: {
      agentAvatarURLs: bootstrapAgentAvatarURLs,
    },
    templateCatalog: bootstrap.template_catalog ?? null,
    credits: bootstrap.credits,
  };
}

export async function ensureBridgeChatConversation(): Promise<WebAgentConversationInfo> {
  const conversation = await fetchJSON<AgentConversationResponse>(
    '/v1/user/bridge-chat',
    {
      method: 'POST',
      headers: jsonHeaders,
    }
  );

  return normalizeAgentConversation(conversation);
}

export async function createAgentConversation(
  name = ''
): Promise<WebConversationListItem> {
  const conversation = await fetchJSON<AgentConversationResponse>(
    '/v1/user/agent/conversations',
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ title: name }),
    }
  );

  return normalizeConversationListItem(conversation, name);
}

export const DEFAULT_AGENT_CONVERSATION_LIMIT = 50;

type AgentConversationResponse = {
  conversation_id: string;
  agent_group_id?: string | null;
  kind: string;
  title: string;
  status: string;
  activity_status?: string | null;
  last_round_error?: string | null;
  created_by_agent_id: string;
  created_by_session_id: string;
  participants?: AgentConversationParticipantResponse[] | null;
  message_count: number;
  target_message_count: number;
  created_at: number;
  updated_at: number;
};

type AgentConversationParticipantResponse = {
  participant_id: string;
  conversation_id?: string | null;
  actor_type: string;
  user_id?: string | null;
  agent_id?: string | null;
  agent_name?: string | null;
  agent_avatar_url?: string | null;
  session_id?: string | null;
  role_label?: string | null;
  state?: string | null;
  wake_on_message?: boolean | null;
  notification_mode?: string | null;
  created_at?: number | null;
  updated_at?: number | null;
};

type AgentConversationMessageResponse = {
  message_id: string;
  kind: string;
  participant_id?: string | null;
  actor_type?: string | null;
  user_id?: string | null;
  agent_id?: string | null;
  agent_name?: string | null;
  agent_avatar_url?: string | null;
  session_id?: string | null;
  role_label?: string | null;
  content: unknown;
  metadata?: unknown;
  created_at: number;
};

type AgentConversationPageResponse = {
  data: AgentConversationResponse[];
  next_cursor?: string | null;
  has_more?: boolean;
};

type AgentCapabilityRequestResponse = {
  request_id: string;
  source_agent_id: string;
  source_session_id: string;
  tool_call_id: string;
  operation_id: string;
  request_type: string;
  status: string;
  request_payload?: Record<string, unknown> | null;
  response_payload?: Record<string, unknown> | null;
  surface_kind?: string | null;
  surface_ref?: Record<string, unknown> | null;
  expires_at?: number | null;
  created_at: number;
  updated_at: number;
  completed_at?: number | null;
};

type AgentCapabilityRequestPageResponse = {
  data: AgentCapabilityRequestResponse[];
};

function normalizeAgentConversation(
  conversation: AgentConversationResponse
): WebAgentConversationInfo {
  return {
    conversationId: conversation.conversation_id,
    agentGroupId: normalizeString(conversation.agent_group_id),
    kind: conversation.kind,
    title: conversation.title,
    status: conversation.status,
    activityStatus: normalizeString(conversation.activity_status),
    lastRoundError: normalizeString(conversation.last_round_error),
    createdByAgentId: conversation.created_by_agent_id,
    createdBySessionId: conversation.created_by_session_id,
    participants: Array.isArray(conversation.participants)
      ? conversation.participants.map(normalizeAgentConversationParticipant)
      : [],
    messageCount: conversation.message_count,
    targetMessageCount: conversation.target_message_count,
    createdAt: conversation.created_at,
    updatedAt: conversation.updated_at,
  };
}

function normalizeAgentConversationPage(
  page: AgentConversationPageResponse | undefined | null
): WebAgentConversationPage {
  return {
    data: (page?.data ?? []).map(normalizeAgentConversation),
    nextCursor: page?.next_cursor?.trim() || null,
    hasMore: page?.has_more ?? false,
  };
}

function normalizeAgentConversationParticipant(
  participant: AgentConversationParticipantResponse
): WebAgentConversationParticipant {
  const agentId = normalizeString(participant.agent_id);
  return {
    participantId: participant.participant_id,
    conversationId: normalizeString(participant.conversation_id),
    actorType: participant.actor_type,
    userId: normalizeString(participant.user_id),
    agentId,
    agentName: normalizeString(participant.agent_name),
    agentAvatarURL: normalizeAgentAvatarURL(
      participant.agent_avatar_url,
      agentId
    ),
    sessionId: normalizeString(participant.session_id),
    roleLabel: normalizeString(participant.role_label),
    state: normalizeString(participant.state) ?? 'active',
    wakeOnMessage: participant.wake_on_message ?? true,
    notificationMode: normalizeString(participant.notification_mode),
    createdAt: participant.created_at ?? 0,
    updatedAt: participant.updated_at ?? 0,
  };
}

function normalizeAgentConversationMessage(
  message: AgentConversationMessageResponse
): WebAgentConversationMessage {
  const agentId = normalizeString(message.agent_id);
  return {
    messageId: message.message_id,
    kind: message.kind,
    participantId: message.participant_id ?? '',
    actorType: message.actor_type ?? '',
    userId: normalizeString(message.user_id),
    agentId,
    agentName: normalizeString(message.agent_name),
    agentAvatarURL: normalizeAgentAvatarURL(message.agent_avatar_url, agentId),
    sessionId: normalizeString(message.session_id),
    roleLabel: normalizeString(message.role_label),
    content: message.content,
    metadata: message.metadata,
    createdAt: message.created_at,
  };
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isAgentConversationPayload(
  payload: Record<string, unknown>
): payload is AgentConversationResponse {
  return (
    typeof payload.conversation_id === 'string' &&
    typeof payload.kind === 'string' &&
    typeof payload.title === 'string' &&
    typeof payload.status === 'string' &&
    typeof payload.message_count === 'number' &&
    typeof payload.target_message_count === 'number' &&
    typeof payload.created_at === 'number' &&
    typeof payload.updated_at === 'number'
  );
}

function eventConversationPayload(
  raw: unknown
): AgentConversationResponse | null {
  if (!isObjectRecord(raw)) {
    return null;
  }
  const wrappedConversation = raw.conversation;
  const payload = isObjectRecord(wrappedConversation)
    ? wrappedConversation
    : raw;
  return isAgentConversationPayload(payload) ? payload : null;
}

function eventConversationId(raw: unknown): string {
  if (!isObjectRecord(raw)) {
    return '';
  }
  const direct = raw.conversation_id;
  if (typeof direct === 'string') {
    return direct.trim();
  }
  if (isObjectRecord(raw.message)) {
    const messageConversationId = raw.message.conversation_id;
    if (typeof messageConversationId === 'string') {
      return messageConversationId.trim();
    }
  }
  const conversation = eventConversationPayload(raw);
  return conversation?.conversation_id.trim() ?? '';
}

function eventMessageId(raw: unknown): string | null {
  if (!isObjectRecord(raw)) {
    return null;
  }
  const value = isObjectRecord(raw.message)
    ? raw.message.message_id
    : raw.message_id;
  return typeof value === 'string' ? value.trim() || null : null;
}

function isAgentConversationMessagePayload(
  payload: Record<string, unknown>
): payload is AgentConversationMessageResponse {
  return (
    typeof payload.message_id === 'string' &&
    typeof payload.kind === 'string' &&
    'content' in payload &&
    typeof payload.created_at === 'number'
  );
}

function eventConversationMessage(
  raw: unknown
): WebAgentConversationMessage | null {
  if (!isObjectRecord(raw)) {
    return null;
  }
  const wrappedMessage = raw.message;
  const payload = isObjectRecord(wrappedMessage) ? wrappedMessage : raw;
  return isAgentConversationMessagePayload(payload)
    ? normalizeAgentConversationMessage(payload)
    : null;
}

function eventConversationDraft(
  raw: unknown
): WebAgentConversationMessageDraft | null {
  if (!isObjectRecord(raw)) {
    return null;
  }
  const conversationId = eventConversationId(raw);
  const draftId = typeof raw.draft_id === 'string' ? raw.draft_id.trim() : '';
  const status = typeof raw.status === 'string' ? raw.status.trim() : '';
  if (!conversationId || !draftId || !status) {
    return null;
  }
  const agentId =
    typeof raw.agent_id === 'string' ? raw.agent_id.trim() || null : null;
  const agentAvatarURL =
    typeof raw.agent_avatar_url === 'string'
      ? raw.agent_avatar_url.trim() || null
      : typeof raw.agentAvatarURL === 'string'
        ? raw.agentAvatarURL.trim() || null
        : null;
  return {
    conversationId,
    draftId,
    toolCallId:
      typeof raw.tool_call_id === 'string'
        ? raw.tool_call_id.trim() || null
        : null,
    kind: typeof raw.kind === 'string' ? raw.kind : 'message',
    participantId:
      typeof raw.participant_id === 'string'
        ? raw.participant_id.trim() || null
        : null,
    actorType:
      typeof raw.actor_type === 'string' ? raw.actor_type.trim() || null : null,
    userId: typeof raw.user_id === 'string' ? raw.user_id.trim() || null : null,
    agentId,
    agentName:
      typeof raw.agent_name === 'string' ? raw.agent_name.trim() || null : null,
    agentAvatarURL: normalizeAgentAvatarURL(agentAvatarURL, agentId),
    sessionId:
      typeof raw.session_id === 'string' ? raw.session_id.trim() || null : null,
    roleLabel:
      typeof raw.role_label === 'string' ? raw.role_label.trim() || null : null,
    content: 'content' in raw ? raw.content : undefined,
    delta: typeof raw.delta === 'string' ? raw.delta : null,
    status,
    createdAt: typeof raw.created_at === 'number' ? raw.created_at : null,
    updatedAt: typeof raw.updated_at === 'number' ? raw.updated_at : null,
  };
}

function normalizeAgentCapabilityRequest(
  request: AgentCapabilityRequestResponse
): WebAgentCapabilityRequest {
  return {
    requestId: request.request_id,
    sourceAgentId: request.source_agent_id,
    sourceSessionId: request.source_session_id,
    toolCallId: request.tool_call_id,
    operationId: request.operation_id,
    requestType: request.request_type,
    status: request.status,
    requestPayload: request.request_payload ?? {},
    responsePayload: request.response_payload ?? {},
    surfaceKind: request.surface_kind ?? null,
    surfaceRef: request.surface_ref ?? {},
    expiresAt: request.expires_at ?? null,
    createdAt: request.created_at,
    updatedAt: request.updated_at,
    completedAt: request.completed_at ?? null,
  };
}

export async function listAgentConversationPage(options: {
  agentGroupId: string;
  limit?: number;
  cursor?: string | null;
}): Promise<WebAgentConversationPage> {
  const limit = options.limit ?? DEFAULT_AGENT_CONVERSATION_LIMIT;
  const query = new URLSearchParams({ limit: String(limit) });
  const cursor = options.cursor?.trim();
  if (cursor) {
    query.set('cursor', cursor);
  }
  const page = await fetchJSON<AgentConversationPageResponse>(
    `${agentConversationBasePath(options.agentGroupId)}?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );

  return normalizeAgentConversationPage(page);
}

export async function listAgentConversations(options: {
  agentGroupId: string;
  limit?: number;
}): Promise<WebAgentConversationInfo[]> {
  return (await listAgentConversationPage(options)).data;
}

export async function getAgentConversation(
  conversationId: string,
  options: { agentGroupId: string }
): Promise<WebAgentConversationInfo> {
  const conversation = await fetchJSON<AgentConversationResponse>(
    `${agentConversationBasePath(options.agentGroupId)}/${encodeURIComponent(conversationId)}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );
  return normalizeAgentConversation(conversation);
}

export async function listAgentConversationMessages(
  conversationId: string,
  options: { agentGroupId: string; limit?: number }
): Promise<WebAgentConversationMessage[]> {
  const limit = options.limit ?? 200;
  const messages = await fetchJSON<AgentConversationMessageResponse[]>(
    `${agentConversationBasePath(options.agentGroupId)}/${encodeURIComponent(conversationId)}/messages?limit=${encodeURIComponent(String(limit))}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );

  return messages.map(normalizeAgentConversationMessage);
}

export async function renameAgentConversation(
  conversationId: string,
  options: { agentGroupId: string; title: string }
): Promise<WebConversationListItem> {
  const conversation = await fetchJSON<AgentConversationResponse>(
    `${agentConversationBasePath(options.agentGroupId)}/${encodeURIComponent(conversationId)}`,
    {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify({ title: options.title }),
    }
  );

  return normalizeConversationListItem(conversation, options.title);
}

export async function deleteAgentConversation(
  conversationId: string,
  options: { agentGroupId: string }
): Promise<void> {
  await fetchVoid(
    `${agentConversationBasePath(options.agentGroupId)}/${encodeURIComponent(conversationId)}`,
    {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
      },
    }
  );
}

export async function getAgentSessionTrace(
  sessionId: string,
  options?: { agentId?: string | null; limit?: number }
): Promise<WebAgentSessionTrace> {
  const searchParams = new URLSearchParams();
  if (options?.agentId?.trim()) {
    searchParams.set('agent_id', options.agentId.trim());
  }
  if (options?.limit && Number.isFinite(options.limit)) {
    searchParams.set('limit', String(Math.max(1, Math.floor(options.limit))));
  }
  const suffix = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
  const trace = await fetchJSON<{
    tool_calls?: Array<{
      call_id?: string;
      name: string;
      status?: string;
      timestamp?: string;
      duration_ms?: number;
      input?: string;
      input_truncated?: boolean;
      output?: string;
      output_truncated?: boolean;
    }>;
    skill_reads?: Array<{
      path: string;
      timestamp?: string;
    }>;
    usage?: {
      prompt_tokens?: number;
      completion_tokens?: number;
      total_tokens?: number;
    };
    has_more?: boolean;
  }>(
    `/v1/user/agent/sessions/${encodeURIComponent(sessionId)}/trace${suffix}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );

  return {
    toolCalls: (trace.tool_calls ?? []).map(call => ({
      callId: call.call_id,
      name: call.name,
      status: call.status,
      timestamp: call.timestamp,
      durationMs: call.duration_ms,
      input: call.input,
      inputTruncated: call.input_truncated ?? false,
      output: call.output,
      outputTruncated: call.output_truncated ?? false,
    })),
    skillReads: (trace.skill_reads ?? []).map(read => ({
      path: read.path,
      timestamp: read.timestamp,
    })),
    usage: {
      promptTokens: trace.usage?.prompt_tokens,
      completionTokens: trace.usage?.completion_tokens,
      totalTokens: trace.usage?.total_tokens,
    },
    hasMore: trace.has_more ?? false,
  };
}

type RawAgentActivity = {
  agent_id?: string | null;
  agentId?: string | null;
  session_id?: string | null;
  sessionId?: string | null;
  conversation_id?: string | null;
  conversationId?: string | null;
  goal?: string | null;
  action?: string | null;
  display_strength?: string | null;
  displayStrength?: string | null;
  display_priority?: string | null;
  displayPriority?: string | null;
  display_hold_ms?: number | null;
  displayHoldMs?: number | null;
  phase?: string;
  summary?: string | null;
  status?: string | null;
  tool_call_id?: string | null;
  toolCallId?: string | null;
  tool_name?: string | null;
  toolName?: string | null;
  sequence?: number | null;
  updated_at?: number | null;
  updatedAt?: number | null;
};

function mapAgentActivity(
  activity: RawAgentActivity
): WebAgentAssistantActivity {
  return {
    agentId: normalizeString(activity.agent_id ?? activity.agentId),
    sessionId: normalizeString(activity.session_id ?? activity.sessionId),
    conversationId: normalizeString(
      activity.conversation_id ?? activity.conversationId
    ),
    goal: activity.goal ?? null,
    action: activity.action ?? null,
    displayStrength:
      activity.display_strength ?? activity.displayStrength ?? null,
    displayPriority:
      activity.display_priority ?? activity.displayPriority ?? null,
    displayHoldMs: activity.display_hold_ms ?? activity.displayHoldMs ?? null,
    phase: activity.phase ?? '',
    summary: activity.summary ?? null,
    status: activity.status ?? null,
    toolCallId: activity.tool_call_id ?? activity.toolCallId ?? null,
    toolName: activity.tool_name ?? activity.toolName ?? null,
    sequence: activity.sequence ?? 0,
    updatedAt: activity.updated_at ?? activity.updatedAt ?? Date.now() / 1000,
  };
}

export async function listAgentActivities(): Promise<
  WebAgentAssistantActivity[]
> {
  const activities = await fetchJSON<RawAgentActivity[]>(
    '/v1/user/agent/activities',
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );
  return activities.map(mapAgentActivity);
}

type WebSSEListener = (event: MessageEvent) => void;

interface WebSSEEventSource {
  addEventListener(type: string, listener: WebSSEListener): void;
}

class FetchSSEEventSource implements WebSSEEventSource {
  private readonly listenersByType = new Map<string, Set<WebSSEListener>>();

  addEventListener(type: string, listener: WebSSEListener): void {
    const listeners = this.listenersByType.get(type) ?? new Set();
    listeners.add(listener);
    this.listenersByType.set(type, listeners);
  }

  dispatch(type: string, data: string, lastEventId: string): void {
    const listeners = this.listenersByType.get(type);
    if (!listeners?.size) return;
    const event =
      typeof MessageEvent === 'function'
        ? new MessageEvent(type, { data, lastEventId })
        : ({ type, data, lastEventId } as MessageEvent);
    for (const listener of Array.from(listeners)) {
      listener(event);
    }
  }
}

class FetchSSEParser {
  private bufferedText = '';
  private eventName = '';
  private eventId = '';
  private dataLines: string[] = [];

  constructor(private readonly eventSource: FetchSSEEventSource) {}

  push(text: string): void {
    this.bufferedText += text;
    while (true) {
      const lineEnd = this.bufferedText.indexOf('\n');
      if (lineEnd === -1) return;
      let line = this.bufferedText.slice(0, lineEnd);
      this.bufferedText = this.bufferedText.slice(lineEnd + 1);
      if (line.endsWith('\r')) {
        line = line.slice(0, -1);
      }
      this.consumeLine(line);
    }
  }

  flush(): void {
    if (this.bufferedText) {
      this.consumeLine(this.bufferedText);
      this.bufferedText = '';
    }
  }

  private consumeLine(line: string): void {
    if (line === '') {
      this.dispatchEvent();
      return;
    }
    if (line.startsWith(':')) {
      return;
    }

    const colonIndex = line.indexOf(':');
    const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
    let value = colonIndex === -1 ? '' : line.slice(colonIndex + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }

    switch (field) {
      case 'event':
        this.eventName = value;
        break;
      case 'data':
        this.dataLines.push(value);
        break;
      case 'id':
        this.eventId = value;
        break;
    }
  }

  private dispatchEvent(): void {
    if (this.dataLines.length === 0) {
      this.eventName = '';
      return;
    }
    this.eventSource.dispatch(
      this.eventName || 'message',
      this.dataLines.join('\n'),
      this.eventId
    );
    this.eventName = '';
    this.dataLines = [];
  }
}

function sseErrorEvent(error: unknown): Event {
  const event = new Event('error');
  Object.defineProperty(event, 'error', {
    configurable: true,
    value: error,
  });
  return event;
}

async function openFetchSSEStream(
  url: string,
  eventSource: FetchSSEEventSource,
  abortController: AbortController,
  onOpen?: () => void
): Promise<void> {
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const clearIdleTimer = () => {
    if (!idleTimer) return;
    clearTimeout(idleTimer);
    idleTimer = null;
  };
  const resetIdleTimer = () => {
    clearIdleTimer();
    idleTimer = setTimeout(() => {
      const idleError = new Error('SSE stream idle timeout');
      abortController.abort(idleError);
      void reader?.cancel(idleError).catch(() => {});
    }, WEB_SSE_IDLE_TIMEOUT_MS);
  };

  try {
    const response = await fetch(url, {
      credentials: 'include',
      headers: withWillowClientContextHeaders({
        accept: 'text/event-stream',
      }),
      signal: abortController.signal,
    });
    if (!response.ok) {
      const body = await readErrorBody(response);
      throw new HTTPStatusError(
        response.status,
        buildErrorMessage(response.status, body),
        errorCode(body),
        Number(response.headers.get('retry-after')) || null
      );
    }
    if (!response.body) {
      throw new Error('SSE response body is missing.');
    }

    onOpen?.();
    resetIdleTimer();

    const parser = new FetchSSEParser(eventSource);
    const decoder = new TextDecoder();
    reader = response.body.getReader();
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) break;
      resetIdleTimer();
      parser.push(decoder.decode(value, { stream: true }));
    }
    const remaining = decoder.decode();
    if (remaining) {
      parser.push(remaining);
    }
    parser.flush();
  } finally {
    clearIdleTimer();
    reader?.releaseLock();
  }
}

function subscribeEventSourceWithReconnect(
  url: string | (() => string),
  bind: (source: WebSSEEventSource) => void,
  onError?: (error: Event) => void,
  onOpen?: () => void
): () => void {
  let disposed = false;
  let abortController: AbortController | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = 1000;
  let connectionGeneration = 0;

  const scheduleReconnect = (generation: number, error: unknown) => {
    if (disposed || generation !== connectionGeneration || reconnectTimer) {
      return;
    }
    abortController = null;
    onError?.(sseErrorEvent(error));
    const delay = retryDelayMs;
    retryDelayMs = Math.min(retryDelayMs * 2, 15000);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const connect = () => {
    if (disposed) return;
    const currentGeneration = connectionGeneration + 1;
    connectionGeneration = currentGeneration;
    const currentAbortController = new AbortController();
    const currentEventSource = new FetchSSEEventSource();
    abortController = currentAbortController;
    bind(currentEventSource);
    void openFetchSSEStream(
      typeof url === 'function' ? url() : url,
      currentEventSource,
      currentAbortController,
      () => {
        if (disposed || connectionGeneration !== currentGeneration) return;
        retryDelayMs = 1000;
        onOpen?.();
      }
    ).then(
      () => {
        scheduleReconnect(
          currentGeneration,
          new Error('SSE stream disconnected')
        );
      },
      error => {
        scheduleReconnect(currentGeneration, error);
      }
    );
  };

  connect();

  return () => {
    disposed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    abortController?.abort();
    abortController = null;
  };
}

export function subscribeAgentActivities(
  onActivity: (activity: WebAgentAssistantActivity) => void,
  onError?: (error: Event) => void,
  onOpen?: () => void
): () => void {
  return subscribeEventSourceWithReconnect(
    '/v1/user/agent/activities/stream',
    source => {
      source.addEventListener('activity', event => {
        try {
          const raw = JSON.parse((event as MessageEvent).data) as unknown;
          if (typeof raw !== 'object' || raw === null) {
            return;
          }
          const activity =
            'activity' in raw
              ? (raw as { activity?: RawAgentActivity | null }).activity
              : (raw as RawAgentActivity);
          if (activity) {
            onActivity(mapAgentActivity(activity));
          }
        } catch {
          // Ignore malformed stream frames.
        }
      });
    },
    onError,
    onOpen
  );
}

export function subscribeAgentConversationEvents(options: {
  agentGroupId: string;
  onEvent: (event: WebAgentConversationEvent) => void;
  onOpen?: () => void;
  onError?: (error: Event) => void;
}): () => void {
  const basePath = `${agentConversationBasePath(options.agentGroupId)}/events`;
  return subscribeSharedAgentConversationEvents(basePath, options);
}

interface AgentConversationEventSubscriber {
  readonly id: number;
  readonly onEvent: (event: WebAgentConversationEvent) => void;
  readonly onOpen?: () => void;
  readonly onError?: (error: Event) => void;
}

interface AgentConversationEventStreamState {
  readonly subscribers: Map<number, AgentConversationEventSubscriber>;
  unsubscribeSource: () => void;
  opened: boolean;
}

const agentConversationEventStreams = new Map<
  string,
  AgentConversationEventStreamState
>();
let nextAgentConversationEventSubscriberId = 1;

function disposeAgentConversationEventStreams() {
  for (const state of agentConversationEventStreams.values()) {
    state.unsubscribeSource();
  }
  agentConversationEventStreams.clear();
}

const chatApiHot = (
  import.meta as ImportMeta & {
    hot?: { dispose(callback: () => void): void };
  }
).hot;
chatApiHot?.dispose(disposeAgentConversationEventStreams);

function subscribeSharedAgentConversationEvents(
  basePath: string,
  options: {
    onEvent: (event: WebAgentConversationEvent) => void;
    onOpen?: () => void;
    onError?: (error: Event) => void;
  }
): () => void {
  let state = agentConversationEventStreams.get(basePath);
  if (!state) {
    state = createAgentConversationEventStream(basePath);
    agentConversationEventStreams.set(basePath, state);
  }

  const subscriber: AgentConversationEventSubscriber = {
    id: nextAgentConversationEventSubscriberId++,
    onEvent: options.onEvent,
    onOpen: options.onOpen,
    onError: options.onError,
  };
  state.subscribers.set(subscriber.id, subscriber);

  return () => {
    const current = agentConversationEventStreams.get(basePath);
    if (!current) {
      return;
    }
    current.subscribers.delete(subscriber.id);
    if (current.subscribers.size > 0) {
      return;
    }
    current.unsubscribeSource();
    agentConversationEventStreams.delete(basePath);
  };
}

function createAgentConversationEventStream(
  basePath: string
): AgentConversationEventStreamState {
  const state: AgentConversationEventStreamState = {
    subscribers: new Map(),
    unsubscribeSource: () => {},
    opened: false,
  };
  state.unsubscribeSource = subscribeEventSourceWithReconnect(
    basePath,
    source => {
      bindAgentConversationEventSource(source, event => {
        for (const subscriber of snapshotAgentConversationSubscribers(state)) {
          subscriber.onEvent(event);
        }
      });
    },
    error => {
      state.opened = false;
      for (const subscriber of snapshotAgentConversationSubscribers(state)) {
        subscriber.onError?.(error);
      }
    },
    () => {
      state.opened = true;
      for (const subscriber of snapshotAgentConversationSubscribers(state)) {
        subscriber.onOpen?.();
      }
    }
  );
  return state;
}

function snapshotAgentConversationSubscribers(
  state: AgentConversationEventStreamState
): AgentConversationEventSubscriber[] {
  return Array.from(state.subscribers.values());
}

function bindAgentConversationEventSource(
  source: WebSSEEventSource,
  onEvent: (event: WebAgentConversationEvent) => void
): void {
  source.addEventListener('conversation_upsert', event => {
    const conversationEvent = parseConversationUpsertEvent(event);
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('conversation_delete', event => {
    const conversationEvent = parseConversationDeleteEvent(event);
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('message_created', event => {
    const conversationEvent = parseConversationMessageEvent(
      event,
      'message_created'
    );
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('message_updated', event => {
    const conversationEvent = parseConversationMessageEvent(
      event,
      'message_updated'
    );
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('message_draft_started', event => {
    const conversationEvent = parseConversationDraftEvent(
      event,
      'message_draft_started'
    );
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('message_draft_delta', event => {
    const conversationEvent = parseConversationDraftEvent(
      event,
      'message_draft_delta'
    );
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('message_draft_completed', event => {
    const conversationEvent = parseConversationDraftEvent(
      event,
      'message_draft_completed'
    );
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
  source.addEventListener('message_draft_cancelled', event => {
    const conversationEvent = parseConversationDraftEvent(
      event,
      'message_draft_cancelled'
    );
    if (conversationEvent) {
      onEvent(conversationEvent);
    }
  });
}

function parseConversationUpsertEvent(
  event: Event
): WebAgentConversationEvent | null {
  try {
    const raw = JSON.parse((event as MessageEvent).data) as unknown;
    const conversation = eventConversationPayload(raw);
    if (!conversation) {
      return null;
    }
    const normalizedConversation = normalizeAgentConversation(conversation);
    return {
      conversationId: normalizedConversation.conversationId,
      type: 'conversation_upsert',
      conversation: normalizedConversation,
    };
  } catch {
    return null;
  }
}

function parseConversationDeleteEvent(
  event: Event
): WebAgentConversationEvent | null {
  try {
    const raw = JSON.parse((event as MessageEvent).data) as unknown;
    const conversationId = eventConversationId(raw);
    if (!conversationId) {
      return null;
    }
    return {
      conversationId,
      type: 'conversation_delete',
    };
  } catch {
    return null;
  }
}

function parseConversationMessageEvent(
  event: Event,
  type: 'message_created' | 'message_updated'
): WebAgentConversationEvent | null {
  try {
    const raw = JSON.parse((event as MessageEvent).data) as unknown;
    const conversationId = eventConversationId(raw);
    if (!conversationId) {
      return null;
    }
    const conversation = eventConversationPayload(raw);
    const message = eventConversationMessage(raw);
    return {
      conversationId,
      messageId: eventMessageId(raw),
      type,
      ...(conversation
        ? { conversation: normalizeAgentConversation(conversation) }
        : {}),
      ...(message ? { message } : {}),
    };
  } catch {
    return null;
  }
}

function parseConversationDraftEvent(
  event: Event,
  type:
    | 'message_draft_started'
    | 'message_draft_delta'
    | 'message_draft_completed'
    | 'message_draft_cancelled'
): WebAgentConversationEvent | null {
  try {
    const raw = JSON.parse((event as MessageEvent).data) as unknown;
    const draft = eventConversationDraft(raw);
    if (!draft) {
      return null;
    }
    return {
      conversationId: draft.conversationId,
      type,
      draft,
    };
  } catch {
    return null;
  }
}

export async function listAgentGroupCapabilityRequests(
  agentGroupId: string,
  options: { status?: string; limit?: number } = {}
): Promise<WebAgentCapabilityRequest[]> {
  const query = new URLSearchParams();
  if (options.status) {
    query.set('status', options.status);
  }
  if (options.limit !== undefined) {
    query.set('limit', String(options.limit));
  }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const page = await fetchJSON<AgentCapabilityRequestPageResponse>(
    `${agentGroupCapabilityRequestsBasePath(agentGroupId)}${suffix}`,
    {
      headers: {
        accept: 'application/json',
      },
    }
  );
  return page.data.map(normalizeAgentCapabilityRequest);
}

export function subscribeAgentGroupCapabilityRequests(options: {
  agentGroupId: string;
  onRequest: (request: WebAgentCapabilityRequest) => void;
  onOpen?: () => void;
  onError?: (error: Event) => void;
}): () => void {
  return subscribeEventSourceWithReconnect(
    `${agentGroupCapabilityRequestsBasePath(options.agentGroupId)}/events`,
    source => {
      source.addEventListener('request_upsert', event => {
        try {
          const raw = JSON.parse(
            (event as MessageEvent).data
          ) as AgentCapabilityRequestResponse;
          options.onRequest(normalizeAgentCapabilityRequest(raw));
        } catch {
          // Ignore malformed stream frames.
        }
      });
    },
    options.onError,
    options.onOpen
  );
}

export async function shareAgentGroupLocationCapabilityRequest(
  agentGroupId: string,
  requestId: string,
  payload: unknown
): Promise<WebAgentCapabilityRequest | null> {
  const response = await fetchJSON<AgentCapabilityRequestResponse>(
    `${agentGroupCapabilityRequestsBasePath(agentGroupId)}/${encodeURIComponent(requestId)}/location/share`,
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }
  );
  return response ? normalizeAgentCapabilityRequest(response) : null;
}

export async function confirmAgentGroupOAuthAuthorizationCapabilityRequest(
  agentGroupId: string,
  requestId: string
): Promise<WebAgentCapabilityRequest | null> {
  const response = await fetchJSON<AgentCapabilityRequestResponse>(
    `${agentGroupCapabilityRequestsBasePath(agentGroupId)}/${encodeURIComponent(requestId)}/oauth-authorization/confirm`,
    {
      method: 'POST',
      headers: jsonHeaders,
    }
  );
  return response ? normalizeAgentCapabilityRequest(response) : null;
}

export async function completeAgentGroupInteractiveLoginCapabilityRequest(
  agentGroupId: string,
  requestId: string
): Promise<WebAgentCapabilityRequest | null> {
  const response = await fetchJSON<AgentCapabilityRequestResponse>(
    `${agentGroupCapabilityRequestsBasePath(agentGroupId)}/${encodeURIComponent(requestId)}/interactive-login/complete`,
    {
      method: 'POST',
      headers: jsonHeaders,
    }
  );
  return response ? normalizeAgentCapabilityRequest(response) : null;
}

export async function decideAgentGroupHostAccessCapabilityRequest(
  agentGroupId: string,
  requestId: string,
  decision: { approved: boolean }
): Promise<WebAgentCapabilityRequest | null> {
  const response = await fetchJSON<AgentCapabilityRequestResponse>(
    `${agentGroupCapabilityRequestsBasePath(agentGroupId)}/${encodeURIComponent(requestId)}/host-access/decision`,
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(decision),
    }
  );
  return response ? normalizeAgentCapabilityRequest(response) : null;
}

export async function decideAgentGroupComputerUseStartCapabilityRequest(
  agentGroupId: string,
  requestId: string,
  decision: { approved: boolean; mode?: string }
): Promise<WebAgentCapabilityRequest | null> {
  const response = await fetchJSON<AgentCapabilityRequestResponse>(
    `${agentGroupCapabilityRequestsBasePath(agentGroupId)}/${encodeURIComponent(requestId)}/computer-use-start/decision`,
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(decision),
    }
  );
  return response ? normalizeAgentCapabilityRequest(response) : null;
}

export async function sendAgentMessage(
  conversationId: string,
  content: string | WebAgentMessageContent[],
  optionsOrClientRequestId:
    | string
    | {
        clientRequestId?: string;
        agentGroupId?: string | null;
        kind?: string;
        metadata?: unknown;
      } = {}
): Promise<WebAgentSendMessageResponse> {
  const options =
    typeof optionsOrClientRequestId === 'string'
      ? { clientRequestId: optionsOrClientRequestId, metadata: undefined }
      : optionsOrClientRequestId;
  const clientRequestId = options.clientRequestId ?? createRandomID();
  const normalizedConversationId = normalizeString(conversationId);
  const agentGroupId = normalizeString(options.agentGroupId);
  if (!normalizedConversationId || !agentGroupId) {
    throw new Error('Conversation send target is required');
  }
  const path = `${agentConversationBasePath(agentGroupId)}/${encodeURIComponent(normalizedConversationId)}/messages`;
  const response = await fetchJSON<{
    message_id: string;
    dispatch_status?: string;
  }>(path, {
    method: 'POST',
    headers: {
      ...jsonHeaders,
      'Idempotency-Key': clientRequestId,
    },
    body: JSON.stringify({
      client_request_id: clientRequestId,
      ...(options.kind === undefined ? {} : { kind: options.kind }),
      content: serializeAgentMessageContent(content),
      ...(options.metadata === undefined ? {} : { metadata: options.metadata }),
    }),
  });

  return { messageId: response.message_id };
}

export async function createAudioTranscription({
  file,
  fileName = 'voice-input.wav',
  durationSeconds,
  languageCode,
  signal,
}: {
  file: Blob;
  fileName?: string;
  durationSeconds?: number;
  languageCode?: string | null;
  signal?: AbortSignal;
}): Promise<WebAudioTranscriptionResponse> {
  const formData = new FormData();
  formData.append('file', file, fileName);

  if (
    typeof durationSeconds === 'number' &&
    Number.isFinite(durationSeconds) &&
    durationSeconds > 0
  ) {
    formData.append('duration_seconds', String(durationSeconds));
  }

  const normalizedLanguageCode = languageCode?.trim();
  if (normalizedLanguageCode) {
    formData.append('language_code', normalizedLanguageCode);
  }

  const response = await fetch('/v1/audio/transcriptions', {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: withWillowClientContextHeaders(),
    signal,
  });

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body)
    );
  }

  const result = (await response.json()) as {
    text?: string;
    language_code?: string | null;
    duration_seconds?: number | null;
  };

  return {
    text: result.text?.trim() ?? '',
    languageCode: result.language_code ?? null,
    durationSeconds: result.duration_seconds ?? null,
  };
}

export async function searchAgentMessages(
  query: string,
  limit = 20
): Promise<WebAgentMessageSearchResult[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return [];
  }

  const searchParams = new URLSearchParams({
    q: trimmedQuery,
    limit: String(limit),
  });

  const messages = await fetchJSON<
    Array<{
      message_id: number;
      role: string;
      session_id?: string | null;
      conversation_id?: string | null;
      created_at: number;
      snippet: string;
      score: number;
    }>
  >(`/v1/user/agent/messages/search?${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  });

  return messages.map(message => ({
    messageId: message.message_id,
    role: message.role,
    sessionId: message.session_id ?? null,
    conversationId: normalizeString(message.conversation_id),
    createdAt: message.created_at,
    snippet: message.snippet,
    score: message.score,
  }));
}

export async function searchAgentConversations(
  query: string,
  limit = 20
): Promise<WebAgentConversationSearchResult[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return [];
  }

  const searchParams = new URLSearchParams({
    q: trimmedQuery,
    limit: String(limit),
  });

  const conversations = await fetchJSON<
    Array<{
      conversation_id: string;
      conversation_kind?: string | null;
      title?: string | null;
      message_id?: string | number | null;
      role?: string | null;
      created_at: number;
      updated_at?: number | null;
      snippet?: string | null;
      score?: number | null;
    }>
  >(`/v1/user/agent/conversations/search?${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  });

  return conversations.flatMap(result => {
    const conversationId = normalizeString(result.conversation_id);
    if (!conversationId) {
      return [];
    }

    return [
      {
        conversationId,
        conversationKind: normalizeString(result.conversation_kind),
        title: normalizeString(result.title) ?? '',
        messageId:
          result.message_id === null || result.message_id === undefined
            ? null
            : String(result.message_id),
        role: normalizeString(result.role) ?? 'conversation',
        createdAt: result.created_at,
        updatedAt: result.updated_at ?? null,
        snippet: result.snippet ?? '',
        score: result.score ?? 0,
      },
    ];
  });
}

export async function listAgentFiles(
  filePath = '/'
): Promise<WebAgentFileEntry[]> {
  const entries = await fetchJSON<
    Array<{
      path: string;
      kind: 'file' | 'dir';
      size: number;
      modified_at: number;
    }>
  >(`/v1/user/agent/files${normalizeAgentPath(filePath)}`, {
    method: 'GET',
    headers: {
      accept: 'application/json',
    },
  });

  return entries.map(entry => ({
    path: entry.path,
    kind: entry.kind,
    size: entry.size,
    modifiedAt: entry.modified_at,
  }));
}

export async function uploadAgentFile(
  filePath: string,
  file: Blob
): Promise<WebAgentFileWriteResult> {
  const headers = new Headers(
    withWillowClientContextHeaders({
      accept: 'application/json',
    })
  );
  if (file.type) {
    headers.set('content-type', file.type);
  }

  const response = await fetch(
    `/v1/user/agent/files${normalizeAgentPath(filePath)}`,
    {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: file,
    }
  );

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body)
    );
  }

  const result = (await response.json()) as {
    path: string;
    size: number;
  };
  return {
    path: result.path,
    size: result.size,
  };
}

export async function downloadAgentFile(filePath: string): Promise<Blob> {
  const response = await fetch(buildAgentFileURL(filePath), {
    method: 'GET',
    credentials: 'include',
    headers: withWillowClientContextHeaders(),
  });

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body)
    );
  }

  return response.blob();
}

export async function cancelAgentSession(sessionId: string): Promise<void> {
  await fetchJSON<unknown>(
    `/v1/user/agent/sessions/${encodeURIComponent(sessionId)}/cancel`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
    }
  );
}

export interface InteractiveLoginCompleteResponse {
  status: string;
  operation_id: string;
  cookie_path?: string;
  cookie_count?: number;
  domains?: string[];
}

export async function completeInteractiveLogin(
  sessionId: string,
  loginId: string
): Promise<InteractiveLoginCompleteResponse> {
  return fetchJSON<InteractiveLoginCompleteResponse>(
    `/v1/user/agent/sessions/${encodeURIComponent(sessionId)}/interactive-logins/${encodeURIComponent(loginId)}/complete`,
    {
      method: 'POST',
      headers: jsonHeaders,
    }
  );
}

function normalizeConversationListItem(
  conversation: AgentConversationResponse,
  fallbackName: string
): WebConversationListItem {
  const participants = Array.isArray(conversation.participants)
    ? conversation.participants.map(normalizeAgentConversationParticipant)
    : [];
  return {
    conversationId: conversation.conversation_id,
    agentGroupId: normalizeString(conversation.agent_group_id),
    kind: normalizeString(conversation.kind),
    title: normalizeString(conversation.title) ?? fallbackName,
    createdByAgentId: normalizeString(conversation.created_by_agent_id),
    createdBySessionId: normalizeString(conversation.created_by_session_id),
    createdAt: conversation.created_at,
    updatedAt: conversation.updated_at || conversation.created_at,
    messageCount: conversation.message_count,
    targetMessageCount: conversation.target_message_count,
    participants,
  };
}

export function conversationListItemFromConversation(
  conversation: WebAgentConversationInfo
): WebConversationListItem {
  return {
    conversationId: conversation.conversationId,
    agentGroupId: conversation.agentGroupId,
    kind: conversation.kind,
    title: conversation.title,
    createdByAgentId: conversation.createdByAgentId,
    createdBySessionId: conversation.createdBySessionId,
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt || conversation.createdAt,
    messageCount: conversation.messageCount,
    targetMessageCount: conversation.targetMessageCount,
    participants: conversation.participants ?? [],
  };
}

function normalizeString(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}
